import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "flex flex-col gap-8 md:gap-6" }
const _hoisted_2 = { class: "text-racq-dark-grey text-xl-bold" }
const _hoisted_3 = {
  key: 0,
  class: "text-racq-med-grey text-xl"
}
const _hoisted_4 = {
  key: 1,
  class: "text-racq-med-grey text-lg mt-1"
}
const _hoisted_5 = { class: "premium-section" }
const _hoisted_6 = {
  key: 0,
  class: "text-racq-med-grey text-xl4-bold"
}
const _hoisted_7 = {
  key: 1,
  class: "text-racq-med-grey text-m ml-[4px]"
}
const _hoisted_8 = {
  key: 2,
  class: "mt-3"
}
const _hoisted_9 = {
  key: 0,
  class: "text-block text-racq-dark-grey"
}
const _hoisted_10 = { key: 0 }
const _hoisted_11 = { class: "text-base-bold" }
const _hoisted_12 = { key: 1 }
const _hoisted_13 = {
  key: 1,
  class: "text-block text-racq-dark-grey"
}

import { computed } from 'vue';
import { toCurrency } from '@/utils/convert';
import { replaceBookmarks } from '@/utils/bookmark.replace';
import { usePaymentStore } from '@/store';
import { useI18n } from 'vue-i18n';
import { PaymentFrequency } from '@/enums/payment-frequency';
import InformationBox from '@/components/InformationBox/InformationBox.vue';
import PaymentFrequencyDropdown from '@/components/PaymentFrequencyDropdown/PaymentFrequencyDropdown.vue';
import { InformationBoxTypes } from '@/interfaces/forms/information-box.interface';
import { RichText } from '@sitecore-jss/sitecore-jss-vue';

interface PaymentHeaderTitle {
  primary: string;
  secondary?: string;
  tertiary?: string;
}

interface PaymentHeaderPremium {
  monthly?: number;
  annual?: number;
  frequencyLabel?: string;
  adminFee?: number;
  adminFeeAnnual?: number;
  discount?: {
    monthly?: number;
    annual?: number;
  };
}

interface StepFormInputs {
  paymentHeader?: {
    DetailsTitleMonthly?: { value?: string };
    DetailsTitleAnnual?: { value?: string };
    DetailsDescriptionMonthly?: { value?: string };
    DetailsDescriptionMonthlyNoDiscount?: { value?: string };
    DetailsDescriptionAnnual?: { value?: string };
    DetailsDescriptionAnnualNoDiscount?: { value?: string };
    InfoType?: { value?: InformationBoxTypes };
    InfoboxDescription?: { value?: string };
    InfoboxDescriptionAnnually?: { value?: string };
    DetailsDescriptionText?: { value?: string };
  };
}

interface PaymentHeaderProps {
  titles: PaymentHeaderTitle;
  premiumInfo: PaymentHeaderPremium;
  stepFormInputs: StepFormInputs;
  showPaymentFrequencyDropdown?: boolean;
  overrideInfoBoxType?: InformationBoxTypes | null;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'PaymentHeader',
  props: {
    titles: {},
    premiumInfo: {},
    stepFormInputs: {},
    showPaymentFrequencyDropdown: { type: Boolean },
    overrideInfoBoxType: {}
  },
  setup(__props: any) {

const props = __props;

const paymentStore = usePaymentStore();
const { getLocaleMessage } = useI18n();
const dictionary = getLocaleMessage('en') as Record<string, string>;

const isAnnual = computed(() => paymentStore.selectedPaymentFrequency === PaymentFrequency.Annually);
const premium = computed(() => (isAnnual.value ? props.premiumInfo.annual : props.premiumInfo.monthly));

const getFormValue = (field?: { value?: string }) => field?.value || '';

const detailsDescriptionText = computed(() => getFormValue(props.stepFormInputs.paymentHeader?.DetailsDescriptionText));

const detailsTitle = computed(() => {
  const value = isAnnual.value
    ? getFormValue(props.stepFormInputs.paymentHeader?.DetailsTitleAnnual)
    : getFormValue(props.stepFormInputs.paymentHeader?.DetailsTitleMonthly);

  return value
    ? replaceBookmarks(value, {
        annualPremium: toCurrency(props.premiumInfo.annual) ?? '',
        monthlyPremium: toCurrency(props.premiumInfo.monthly) ?? '',
        onlineDiscount: getDiscountCopy(),
      })
    : '';
});

const detailsDescription = computed(() => {
  let value = isAnnual.value
    ? getFormValue(props.stepFormInputs.paymentHeader?.DetailsDescriptionAnnual)
    : getFormValue(props.stepFormInputs.paymentHeader?.DetailsDescriptionMonthly);

  // Annually without online discount, e.g FTTP
  if (isAnnual.value && !props.premiumInfo?.discount?.annual) {
    value = getFormValue(props.stepFormInputs.paymentHeader?.DetailsDescriptionAnnualNoDiscount);
  }

  // Monthly no discount, e.g FTTP
  if (!isAnnual.value && !props.premiumInfo?.discount?.monthly) {
    value = getFormValue(props.stepFormInputs.paymentHeader?.DetailsDescriptionMonthlyNoDiscount);
  }

  return value
    ? replaceBookmarks(value, {
        annualPremium: toCurrency(props.premiumInfo.annual) ?? '',
        monthlyPremium: toCurrency(props.premiumInfo.monthly) ?? '',
        onlineDiscount: getDiscountCopy(),
        adminFee: toCurrency(props.premiumInfo.adminFee) ?? '',
        adminFeeAnnual: toCurrency(props.premiumInfo.adminFeeAnnual) ?? '',
        instalmentFee: toCurrency(props.premiumInfo.adminFee) ?? '', //backward content compatability
      })
    : '';
});

const infoBoxType = computed(() => {
  if (props.overrideInfoBoxType !== undefined) {
    return props.overrideInfoBoxType ?? undefined;
  }
  return props.stepFormInputs.paymentHeader?.InfoType?.value ?? undefined;
});

const infoBoxMessage = computed(() => {
  const value = isAnnual.value
    ? getFormValue(props.stepFormInputs.paymentHeader?.InfoboxDescriptionAnnually)
    : getFormValue(props.stepFormInputs.paymentHeader?.InfoboxDescription);

  return value
    ? replaceBookmarks(value, {
        annualPremium: toCurrency(props.premiumInfo.annual) ?? '',
        monthlyPremium: toCurrency(props.premiumInfo.monthly) ?? '',
        recurringText: paymentStore.selectedRenewalPreference ? ' a recurring' : 'an',
        adminFee: toCurrency(props.premiumInfo.adminFee) ?? '',
        adminFeeAnnual: toCurrency(props.premiumInfo.adminFeeAnnual) ?? '',
        instalmentFee: toCurrency(props.premiumInfo.adminFee) ?? '', //backward content compatability
      })
    : '';
});

function getDiscountCopy() {
  if (!props.premiumInfo?.discount) return '';
  return isAnnual.value
    ? dictionary.IQ_OnlineDiscount.replace('$#value#', toCurrency(props.premiumInfo.discount.annual, false))
    : dictionary.IQ_OnlineDiscount.replace('$#value#', toCurrency(props.premiumInfo.discount.monthly, false));
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", null, [
      _createElementVNode("h4", _hoisted_2, _toDisplayString(_ctx.titles.primary), 1),
      (_ctx.titles.secondary)
        ? (_openBlock(), _createElementBlock("h5", _hoisted_3, _toDisplayString(_ctx.titles.secondary), 1))
        : _createCommentVNode("", true),
      (_ctx.titles.tertiary)
        ? (_openBlock(), _createElementBlock("h6", _hoisted_4, _toDisplayString(_ctx.titles.tertiary), 1))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", _hoisted_5, [
      (premium.value)
        ? (_openBlock(), _createElementBlock("span", _hoisted_6, _toDisplayString(_unref(toCurrency)(premium.value)), 1))
        : _createCommentVNode("", true),
      (_ctx.premiumInfo.frequencyLabel)
        ? (_openBlock(), _createElementBlock("span", _hoisted_7, _toDisplayString(_ctx.premiumInfo.frequencyLabel), 1))
        : _createCommentVNode("", true),
      (_ctx.showPaymentFrequencyDropdown)
        ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
            _createVNode(PaymentFrequencyDropdown, { outerClass: "" })
          ]))
        : _createCommentVNode("", true)
    ]),
    (detailsTitle.value || detailsDescription.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
          (detailsTitle.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                _createElementVNode("p", _hoisted_11, _toDisplayString(detailsTitle.value), 1)
              ]))
            : _createCommentVNode("", true),
          (detailsDescription.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                _createVNode(_unref(RichText), {
                  field: { value: detailsDescription.value },
                  class: "text-base"
                }, null, 8, ["field"])
              ]))
            : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true),
    (detailsDescriptionText.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
          _createVNode(_unref(RichText), {
            field: { value: detailsDescriptionText.value },
            class: "text-base"
          }, null, 8, ["field"])
        ]))
      : _createCommentVNode("", true),
    (infoBoxType.value)
      ? (_openBlock(), _createBlock(InformationBox, {
          key: 2,
          type: infoBoxType.value,
          description: infoBoxMessage.value
        }, null, 8, ["type", "description"]))
      : _createCommentVNode("", true)
  ]))
}
}

})